import type { ReactNode } from 'react';
import {
  type LayoutChangeEvent,
  Platform,
  type StyleProp,
  useWindowDimensions,
  type ViewStyle,
} from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
} from 'react-native-reanimated';
import { useScrollState } from './ScrollContext';

interface ScrollRevealProps {
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  /** Vertical travel of the reveal, in px. */
  distance?: number;
  /** Starting scale (1 = no scale-up). */
  scaleFrom?: number;
  /** Stagger index — later items resolve later as you scroll, for cascades. */
  index?: number;
  /** Max blur (px) at the start of the reveal; lower for small cards. */
  maxBlur?: number;
}

const canBlur = Platform.OS === 'web';

/**
 * Reveals its content as it scrolls into the viewport: rises, scales and blurs
 * in on the UI thread, driven by the shared scroll offset. Measures its own
 * absolute Y once per layout, so it works inside the deeply-nested landing tree.
 * Degrades to a plain view when there is no scroll context or reduced motion.
 */
export const ScrollReveal = ({
  children,
  style,
  distance = 72,
  scaleFrom = 0.92,
  index = 0,
  maxBlur = 0,
}: ScrollRevealProps) => {
  const scroll = useScrollState();
  const reducedMotion = useReducedMotion();
  const { height } = useWindowDimensions();

  const fallback = useSharedValue(0);
  const offset = scroll?.offset ?? fallback;
  // Absolute Y of this element within the scroll content; -1 until measured.
  const anchorY = useSharedValue(-1);
  const animatedRef = useAnimatedRef<Animated.View>();

  const enabled = !!scroll && !reducedMotion;
  const stagger = index * 55;
  const blurEnabled = canBlur && maxBlur > 0;

  const onLayout = (_e: LayoutChangeEvent) => {
    if (!enabled) return;
    runOnUI(() => {
      'worklet';
      const measured = measure(animatedRef);
      if (measured) {
        anchorY.value = measured.pageY + offset.value;
      }
    })();
  };

  const animatedStyle = useAnimatedStyle(() => {
    if (!enabled) return { opacity: 1 };
    if (anchorY.value < 0) {
      const hidden: Record<string, unknown> = {
        opacity: 0,
        transform: [{ translateY: distance }, { scale: scaleFrom }],
      };
      if (blurEnabled) hidden.filter = `blur(${maxBlur}px)`;
      return hidden;
    }
    const viewportTop = anchorY.value - offset.value;
    // Stagger only the START (later items begin later); the END is shared so
    // EVERY item is fully revealed once the section is comfortably in view —
    // trailing cards never get stranded mid-reveal.
    const start = height * 0.96 - stagger;
    const end = height * 0.5;
    const progress = interpolate(viewportTop, [start, end], [0, 1], Extrapolation.CLAMP);

    const revealed: Record<string, unknown> = {
      opacity: progress,
      transform: [
        { translateY: (1 - progress) * distance },
        { scale: scaleFrom + progress * (1 - scaleFrom) },
      ],
    };
    if (blurEnabled) {
      const blur = (1 - progress) * maxBlur;
      revealed.filter = `blur(${blur}px)`;
    }
    return revealed;
  });

  return (
    <Animated.View ref={animatedRef} onLayout={onLayout} style={[style, animatedStyle]}>
      {children}
    </Animated.View>
  );
};
